import React, { useEffect, useCallback, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useVisaAuth from "../../hooks/useVisaAuth";
import useUser from "../../hooks/useUser";
import Model from "@tripian/model";
import { api } from "@tripian/core";
import {
  TRIPS,
  TRIP_CLONE,
  DELETE_USER,
  LOGIN,
} from "../../constants/ROUTER_PATH_TITLE";
import { useDispatch } from "react-redux";
import { changeUser } from "../../redux/action/user";
import { PreLoading } from "@tripian/react";
import {
  ApiResponsePayload,
  PostMessageEvent,
  RedirectUserPayload,
  MessageType,
} from "./VisaPostMessageTypes";
import VisaErrorModal from "./VisaErrorModal";
import useTranslate from "../../hooks/useTranslate";
import { getLocalizedErrorContentFromCode } from "./errorMapping";
import * as gtag from "../../gtag";
import { trackEvent } from "../../tracking/eventTracking";
import classes from "./VisaAuth.module.scss";

const VCES_LABEL = "vces";

const slugifyErrorGroup = (group: string): string =>
  group.toLowerCase().replace(/\s+/g, "_");

// Mirrors the GA verification_returned_* events into the Event Tracking API,
// so the funnel's "continue-clicked → returned-verified" rate is queryable on our side.
const trackVerificationError = (reason: string) => {
  gtag.googleAnalyticsEvent("verification_returned_error", { reason });
  trackEvent("verification_returned", {
    label: VCES_LABEL,
    metadata: { outcome: "error", reason },
  });
};

// Errors raised inside Visa's form (e.g. its service/info call failing) are invisible to us otherwise;
// this goes to the #logs Slack channel. No user data, only what Visa reports.
// Written so the line can be forwarded to Visa as is: Slack's own timestamp is lost when copied, so a UTC time is included.
const alertVisaApiError = (response: ApiResponsePayload["response"]) => {
  api
    .log(
      `Visa verification error | requisitionId=${response?.requisitionId} | time=${new Date().toISOString()} | host=${window.location.hostname} | errorCode=${response?.errorCode} | httpCode=${response?.httpCode} | message=${response?.message}`
    )
    .catch(err => console.warn("Failed to send Visa error log:", err));
};

const getOriginFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.hostname}`;
  } catch (error) {
    return "";
  }
};

const getAllowedOrigins = (): string[] => {
  const authFormUrl = window.tconfig?.AUTH_FORM_URL;
  const defaultOrigins = [
    "https://epm.lacbenefits.visa.com",
    "https://beneficioslac.visa.com",
  ];
  if (authFormUrl) {
    const origin = getOriginFromUrl(authFormUrl);
    return origin ? [origin] : defaultOrigins;
  }
  return defaultOrigins;
};

const isValidMessageType = (type: unknown): type is MessageType => {
  const validTypes = [
    "API_RESPONSE",
    "REDIRECT_USER",
    "USER_BLOCKED",
    "SESSION_EXPIRED",
  ] as const;
  return typeof type === "string" && validTypes.includes(type as MessageType);
};

const isValidMessageStructure = (
  message: unknown
): message is PostMessageEvent => {
  if (!message || typeof message !== "object") return false;

  const msg = message as any;

  if (!("messageType" in msg) || !isValidMessageType(msg.messageType))
    return false;

  switch (msg.messageType) {
    case "API_RESPONSE":
      return (
        typeof msg.payload === "object" &&
        msg.payload !== null &&
        "data" in msg.payload &&
        "response" in msg.payload &&
        typeof msg.payload.response === "object" &&
        "hasError" in msg.payload.response
      );

    case "REDIRECT_USER":
      return (
        typeof msg.payload === "object" &&
        msg.payload !== null &&
        typeof msg.payload.url === "string"
      );

    case "USER_BLOCKED":
    case "SESSION_EXPIRED":
      return msg.payload === null;

    default:
      return false;
  }
};

const VisaAuth = () => {
  const { user } = useUser();
  const {
    loadingVisaToken,
    visaToken,
    hasError,
    validateVisa,
    refreshVisaToken,
    resetError,
  } = useVisaAuth(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const [errorMessage, setErrorMessage] = useState<{
    title: string | null;
    description: string | null;
  } | null>(null);
  const [showIframe, setShowIframe] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const sessionExpiredTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [sessionExpiredReceived, setSessionExpiredReceived] = useState(false);

  const useQuery = <T extends { [K in keyof T]?: string } = {}>(): T => {
    const query = new URLSearchParams(location.search);
    const result: T = {} as T;
    query.forEach((val, key) => {
      result[key] = val;
    });
    return result;
  };

  const { sharedTripHash } = useQuery<{ sharedTripHash?: string }>();

  const redirectAfterVisa = useCallback(() => {
    if (sharedTripHash) {
      navigate(`${TRIP_CLONE.PATH}/${sharedTripHash}`, { replace: true });
    } else if (
      (location.state as { from?: string })?.from === DELETE_USER.PATH
    ) {
      navigate(DELETE_USER.PATH, { replace: true });
    } else {
      navigate(TRIPS.PATH, { replace: true });
    }
  }, [sharedTripHash, location.state, navigate]);

  const redirectToLogin = useCallback(
    (title?: string | null, description?: string | null) => {
      const hasTwoParams = description !== undefined;
      const resolvedTitle = hasTwoParams ? (title ?? null) : null;
      const resolvedDescription = hasTwoParams
        ? (description ?? null)
        : (title ?? null);

      if (resolvedTitle || resolvedDescription) {
        setShowIframe(false);
        setErrorMessage({
          title: resolvedTitle,
          description: resolvedDescription,
        });
        setTimeout(() => {
          setShowIframe(false);
          setErrorMessage(null);
          setShouldRedirect(true);
        }, 10000);
        return;
      }

      setShowIframe(false);
      setErrorMessage(null);
      setShouldRedirect(true);
    },
    []
  );

  useEffect(() => {
    if (shouldRedirect) {
      setErrorMessage(null);
      navigate(LOGIN.PATH, {
        state: { from: location.pathname },
        replace: true,
      });
    }
  }, [shouldRedirect, navigate, location.pathname]);

  const handleApiResponse = useCallback(
    async (payload: ApiResponsePayload) => {
      try {
        if (payload.response?.hasError) {
          console.error("API ERROR:", {
            errorCode: payload.response.errorCode,
            message: payload.response.message,
            requisitionId: payload.response.requisitionId,
            httpCode: payload.response.httpCode,
          });

          const localized = getLocalizedErrorContentFromCode(
            t,
            payload.response.errorCode
          );
          trackVerificationError(slugifyErrorGroup(localized.group));
          alertVisaApiError(payload.response);
          redirectToLogin(localized.title, localized.description);
          return;
        }

        if (!payload.data?.content) {
          console.warn("Empty Content", {
            requisitionId: payload.response?.requisitionId,
          });
          trackVerificationError("empty_content");
          redirectToLogin(null, t("visa.auth.emptyContent"));
          return;
        }

        try {
          const result = await validateVisa(payload.data.content);

          if (!result) {
            console.error("Empty Validation Result", {
              requisitionId: payload.response?.requisitionId,
            });
            trackVerificationError("system_error");
            redirectToLogin(null, t("visa.auth.systemError"));
            return;
          }

          if (result.userType === Model.USER_TYPE.ELIGIBILITY) {
            if (sessionExpiredTimeoutRef.current) {
              clearTimeout(sessionExpiredTimeoutRef.current);
              sessionExpiredTimeoutRef.current = null;
            }

            setSessionExpiredReceived(false);

            setErrorMessage(null);
            setShowIframe(true);

            gtag.googleAnalyticsEvent("verification_returned_success", {});
            trackEvent("verification_returned", {
              label: VCES_LABEL,
              metadata: { outcome: "success" },
            });
            dispatch(changeUser(result));
            redirectAfterVisa();
          } else {
            console.warn("User is not eligible", {
              requisitionId: payload.response?.requisitionId,
            });
            trackVerificationError("not_eligible");
            redirectToLogin(null, t("visa.auth.notEligible"));
          }
        } catch (validationError) {
          console.error("Validation Error:", validationError, {
            requisitionId: payload.response?.requisitionId,
          });
          trackVerificationError("system_error");
          redirectToLogin(null, t("visa.auth.systemError"));
        }
      } catch (error) {
        trackVerificationError("system_error");
        redirectToLogin(null, t("visa.auth.systemError"));
      }
    },
    [dispatch, validateVisa, redirectAfterVisa, redirectToLogin, t]
  );

  const handleRedirectUser = useCallback(
    (payload: RedirectUserPayload) => {
      const url = payload?.url;
      if (typeof url === "string" && url.startsWith("http")) {
        window.location.href = url;
      } else {
        trackVerificationError("invalid_redirect_url");
        redirectToLogin(null, t("visa.auth.invalidRedirectUrl"));
      }
    },
    [redirectToLogin, t]
  );

  const handleUserBlocked = useCallback(() => {
    trackVerificationError("user_blocked");
    redirectToLogin(null, t("visa.auth.accountBlocked"));
  }, [redirectToLogin, t]);

  const handleSessionExpired = useCallback(async () => {
    setSessionExpiredReceived(true);

    setTimeout(() => {
      if (sessionExpiredReceived) {
        setShowIframe(false);
        setErrorMessage({
          title: null,
          description: t("visa.auth.sessionExpired"),
        });
        trackVerificationError("session_expired");

        if (sessionExpiredTimeoutRef.current) {
          clearTimeout(sessionExpiredTimeoutRef.current);
        }

        sessionExpiredTimeoutRef.current = setTimeout(() => {
          setShouldRedirect(true);
        }, 10000);
      }
    }, 5000);
  }, [t, sessionExpiredReceived]);

  useEffect(() => {
    const handleVisaMessage = async (event: MessageEvent) => {
      const allowedOrigins = getAllowedOrigins();
      if (!allowedOrigins.includes(event.origin)) {
        console.warn("Invalid origin:", event.origin);
        return;
      }

      try {
        let rawMessage: unknown;
        try {
          rawMessage =
            typeof event.data === "string"
              ? JSON.parse(event.data)
              : event.data;
        } catch (parseError) {
          console.error("JSON parse error:", parseError);
          return;
        }

        // Filter out messages without messageType (extension messages, etc.)
        if (!(rawMessage as any)?.messageType) {
          return;
        }

        if (!isValidMessageStructure(rawMessage)) {
          console.error("Invalid message structure");
          return;
        }

        const message: PostMessageEvent = rawMessage;

        switch (message.messageType) {
          case "API_RESPONSE":
            await handleApiResponse(message.payload);
            break;

          case "REDIRECT_USER":
            if (!message.payload?.url?.startsWith("http")) {
              trackVerificationError("invalid_redirect_url");
              redirectToLogin(null, t("visa.auth.invalidRedirectUrl"));
              return;
            }
            handleRedirectUser(message.payload);
            break;

          case "USER_BLOCKED":
            if (message.payload !== null) {
              return;
            }
            handleUserBlocked();
            break;

          case "SESSION_EXPIRED":
            if (message.payload !== null) {
              return;
            }
            await handleSessionExpired();
            break;

          default:
            console.warn(
              "Unknown message type:",
              (message as PostMessageEvent).messageType
            );
            trackVerificationError("unknown_message_type");
            redirectToLogin(null, t("visa.auth.unknownError"));
        }
      } catch (error) {
        trackVerificationError("system_error");
        redirectToLogin(null, t("visa.auth.invalidData"));
      }
    };

    window.addEventListener("message", handleVisaMessage);
    return () => {
      window.removeEventListener("message", handleVisaMessage);
    };
  }, [
    dispatch,
    validateVisa,
    handleApiResponse,
    handleRedirectUser,
    handleUserBlocked,
    handleSessionExpired,
    redirectToLogin,
    t,
  ]);

  useEffect(() => {
    if (visaToken) {
      setShowIframe(true);
      setErrorMessage(null);
      setShouldRedirect(false);
    }
  }, [visaToken]);

  useEffect(() => {
    if (hasError) {
      trackVerificationError("token_fetch_error");
    }
  }, [hasError]);

  useEffect(() => {
    if (!loadingVisaToken && !visaToken && user && !hasError) {
      const retryToken = async () => {
        try {
          await refreshVisaToken();
        } catch (error) {
          if (window.tconfig?.ELIGIBILITY_TYPE === "visa") {
            setShowIframe(false);
            setErrorMessage({
              title: null,
              description: t("visa.auth.systemError"),
            });
            trackVerificationError("system_error");
            setShouldRedirect(true);
          }
        }
      };
      retryToken();
    }
  }, [loadingVisaToken, visaToken, hasError, t, user, refreshVisaToken]);

  useEffect(() => {
    if (user?.userType === Model.USER_TYPE.ELIGIBILITY) {
      setShowIframe(true);
      setErrorMessage(null);
      setShouldRedirect(false);
      redirectAfterVisa();
    }
  }, [user, redirectAfterVisa]);

  useEffect(() => {
    return () => {
      if (sessionExpiredTimeoutRef.current) {
        clearTimeout(sessionExpiredTimeoutRef.current);
      }
    };
  }, []);

  if (loadingVisaToken || !user) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-40">
        <PreLoading />
      </div>
    );
  }

  return (
    <>
      <div className={classes.iframeContainer}>
        {visaToken && !errorMessage && showIframe && (
          <iframe
            key={visaToken}
            src={`${window.tconfig.AUTH_FORM_URL}/?token=${visaToken}`}
            className={classes.iframe}
            title={t("visa.auth.formTitle")}
            width="100%"
            height="1090"
            allow="clipboard-write"
            onError={e => {
              setShowIframe(false);
              setErrorMessage({
                title: null,
                description: t("visa.auth.systemError"),
              });
              trackVerificationError("iframe_load_error");
              setTimeout(() => {
                setShouldRedirect(true);
              }, 10000);
            }}
          />
        )}
      </div>
      {(errorMessage || hasError) && (
        <VisaErrorModal
          title={errorMessage?.title || undefined}
          description={errorMessage?.description || t("visa.auth.systemError")}
          secondsRemaining={10}
          onManualRedirect={() => {
            setShouldRedirect(true);
          }}
          onRetry={
            hasError
              ? () => {
                  resetError();
                  refreshVisaToken();
                }
              : undefined
          }
        />
      )}
    </>
  );
};

export default VisaAuth;
